import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  pageGenerationInFlight,
  type Book,
  type Page as PageData,
} from "../../api";
import FlipBook, { type FlipBookHandle } from "./FlipBook";
import MobileCarousel from "./MobileCarousel";
import Page from "./Page";
import i18n from "../../i18n";

/**
 * Renders the page narrative and auto-shrinks the font so the WHOLE narrative
 * fits the fixed-height footer slot — no two-line clamp / ellipsis. Most
 * narratives fit at the CSS-defined size; only long ones step down (to a
 * floor), so short pages look unchanged while long ones stay fully readable.
 */
function FitNarrative({ text }: { text: string }) {
  const slotRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const span = textRef.current;
    if (!slot || !span) return;

    const MIN_PX = 12;
    const fit = () => {
      // Skip while the slot is unmeasurable (e.g. an off-screen/virtualized
      // page rendered at zero height) — shrinking against a 0px box would peg
      // the font at the floor. The ResizeObserver refits once it has a real size.
      if (slot.clientHeight === 0) return;
      // Reset to the CSS size for this breakpoint, then shrink until the text
      // fits the slot box. The slot's height is fixed by the footer layout and
      // independent of the narrative, so changing the font never resizes it
      // (no ResizeObserver feedback loop).
      span.style.fontSize = "";
      let size = parseFloat(getComputedStyle(span).fontSize) || 24;
      span.style.fontSize = `${size}px`;
      while (
        size > MIN_PX &&
        (span.scrollHeight > slot.clientHeight || span.scrollWidth > slot.clientWidth)
      ) {
        size -= 1;
        span.style.fontSize = `${size}px`;
      }
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(slot);
    // Web fonts change text metrics once they load — refit when they settle.
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) fit();
    });
    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, [text]);

  return (
    <div ref={slotRef} className="tp-page-narrative">
      <span ref={textRef} className="tp-page-narrative-text">
        {text}
      </span>
    </div>
  );
}

type Slot =
  | { kind: "blank"; label?: string }
  | { kind: "cover"; book: Book; author: string }
  | { kind: "page"; page: PageData; pageNumber: number }
  | { kind: "end"; book: Book };

/**
 * Slot ordering differs by orientation:
 *
 *   Desktop (spread): [dedication, cover, ...pages, end]
 *     - First spread shows [dedication, cover] side-by-side, matching the
 *       inside-front-cover + title-page layout of a real hardcover book.
 *     - `showCover: false` is set on the engine in this mode so slots
 *       pair up from index 0; "The End" follows the last real page and may
 *       land on either side depending on page count.
 *
 *   Mobile (single): [cover, ...pages, end]
 *     - The dedication is dropped — single-page mode would otherwise show
 *       it alone before any context, which reads as a placeholder.
 *     - No spread-parity padding is needed in a one-page carousel; otherwise
 *       spacer pages inserted for desktop book geometry become visible blank
 *       slides before "The End."
 */
function buildSlots(
  book: Book,
  pages: PageData[],
  author: string,
  isDesktop: boolean,
): Slot[] {
  const cover: Slot = { kind: "cover", book, author };
  const end: Slot = { kind: "end", book };
  const pageSlots: Slot[] = pages.map((p, i) => ({
    kind: "page",
    page: p,
    pageNumber: i + 1,
  }));

  if (isDesktop) {
    const dedication: Slot = {
      kind: "blank",
      label: book.author.name
        ? i18n.t("reader.dedication", { name: book.author.name })
        : undefined,
    };
    return [dedication, cover, ...pageSlots, end];
  }

  // Mobile is a single-page carousel, not a facing-page spread. Keep "The End"
  // immediately after the last story/art page so generated spacer pages never
  // appear as blank slides.
  return [cover, ...pageSlots, end];
}

export function useIsDesktop(): boolean {
  const [v, setV] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 760px)").matches
      : true,
  );
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 760px)");
    const handler = (e: MediaQueryListEvent) => setV(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return v;
}

export const BookView = forwardRef<FlipBookHandle, {
  book: Book;
  pages: PageData[];
  ownerControls?: boolean;
  onFixPage?: (page: PageData) => void;
  onFlip?: (index: number) => void;
  /** Open on the first content page (skipping the cover/dedication) so the
   *  art is visible immediately. Used by the landing demo. */
  startAtFirstPage?: boolean;
}>(function BookView({ book, pages, ownerControls = false, onFixPage, onFlip, startAtFirstPage = false }, ref) {
  const { t } = useTranslation();
  const author = book.author.name ?? t("reader.defaultAuthor");
  const isDesktop = useIsDesktop();
  const slots = useMemo(() => buildSlots(book, pages, author, isDesktop), [book, pages, author, isDesktop]);
  const firstPageIndex = slots.findIndex((s) => s.kind === "page");
  const startIndex = startAtFirstPage && firstPageIndex > 0 ? firstPageIndex : 0;
  const flipRef = useRef<FlipBookHandle>(null);
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  useImperativeHandle(ref, () => ({
    flipNext: () => flipRef.current?.flipNext(),
    flipPrev: () => flipRef.current?.flipPrev(),
    getCurrentPageIndex: () => flipRef.current?.getCurrentPageIndex() ?? 0,
    getPageCount: () => flipRef.current?.getPageCount() ?? 0,
  }), []);
  // Orientation flip remounts the engine (FlipBook ⇄ MobileCarousel), which
  // re-seeds at startIndex; keep the counter in sync.
  useEffect(() => { setCurrentIndex(startIndex); }, [isDesktop, startIndex]);
  const handleFlip = (idx: number) => { setCurrentIndex(idx); onFlip?.(idx); };
  const totalPages = slots.length;
  const displayedPageNumber = Math.min(currentIndex + (isDesktop ? 2 : 1), totalPages);
  const noFix = (_p: PageData) => {};
  return (
    <>
      <div className="tp-book">
        {isDesktop ? (
          <FlipBook ref={flipRef} usePortrait={false} onFlip={handleFlip} startPage={startIndex}>
            {slots.map((slot, i) => {
              const isCover = slot.kind === "cover" || slot.kind === "end";
              return (
                <Page key={i} density={isCover ? "hard" : "soft"} variant={isCover ? "cover" : "interior"}>
                  <SlotInner slot={slot} ownerControls={ownerControls} onFixPage={onFixPage ?? noFix} />
                </Page>
              );
            })}
          </FlipBook>
        ) : (
          <MobileCarousel ref={flipRef} onFlip={handleFlip} initialIndex={startIndex}>
            {slots.map((slot, i) => {
              const isCover = slot.kind === "cover" || slot.kind === "end";
              return (
                <Page key={i} variant={isCover ? "cover" : "interior"}>
                  <SlotInner slot={slot} ownerControls={ownerControls} onFixPage={onFixPage ?? noFix} />
                </Page>
              );
            })}
          </MobileCarousel>
        )}
      </div>
      <div className="tp-book-controls">
        <button className="tp-btn" onClick={() => flipRef.current?.flipPrev()}>{t("reader.prev")}</button>
        <div className="tp-eyebrow">{displayedPageNumber} / {totalPages}</div>
        <button className="tp-btn" onClick={() => flipRef.current?.flipNext()}>{t("reader.next")}</button>
      </div>
    </>
  );
});

function SlotInner({
  slot,
  ownerControls,
  onFixPage,
}: {
  slot: Slot;
  ownerControls: boolean;
  onFixPage: (page: PageData) => void;
}) {
  const { t } = useTranslation();
  switch (slot.kind) {
    case "blank":
      return (
        <div className="tp-page-blank">
          {slot.label ? (
            <div className="tp-eyebrow" style={{ fontSize: 16 }}>
              {slot.label}
            </div>
          ) : null}
        </div>
      );
    case "cover": {
      const { book, author } = slot;
      return (
        <div className="tp-page-cover">
          <div className="tp-eyebrow">{t("reader.coverEyebrow")}</div>
          <h1 className="tp-cover-title">{book.title || t("reader.untitled")}</h1>
          <div className="tp-cover-by">{t("reader.byAuthor", { author })}</div>
          <hr className="tp-cover-rule" />
          <div className="tp-cover-mark">✦</div>
        </div>
      );
    }
    case "page": {
      const src = slot.page.polishedUrl ?? slot.page.originalUrl ?? "";
      const transform = slot.page.polishedUrl
        ? "none"
        : `rotate(${slot.page.rotation}deg)`;
      const narrative = (slot.page.storyText ?? "").trim() || null;
      const generation = slot.page.latestGeneration;
      const regenerating = pageGenerationInFlight(generation);
      const regenFailed = generation?.status === "failed";
      return (
        <div className="tp-page-inner">
          <div className="tp-page-image">
            {src ? (
              <img
                src={src}
                alt={t("reader.pageAlt", { number: slot.pageNumber })}
                style={{ transform }}
              />
            ) : (
              <div className="tp-page-image-placeholder" aria-label={t("reader.pageAlt", { number: slot.pageNumber })} />
            )}
            {regenerating && (
              <div className="tp-page-regen-badge">{t("reader.regenerating")}</div>
            )}
          </div>
          <div className="tp-page-footer" data-has-narrative={narrative ? "1" : "0"}>
            {narrative && <FitNarrative text={narrative} />}
            {ownerControls && regenFailed && (
              <div className="tp-page-regen-error">{t("reader.regenFailed")}</div>
            )}
            <div className="tp-page-num">{t("reader.pageNum", { number: slot.pageNumber })}</div>
            {ownerControls && (
              <button
                type="button"
                className="tp-page-fix-btn"
                disabled={regenerating}
                onClick={() => onFixPage(slot.page)}
              >
                {regenerating ? t("reader.fixing") : t("reader.fixPage")}
              </button>
            )}
          </div>
        </div>
      );
    }
    case "end":
      return (
        <div className="tp-page-end">
          <div className="tp-end-title">{t("reader.theEnd")}</div>
          <div className="tp-end-credit">
            {slot.book.author.name ? (
              <Trans
                i18nKey="reader.madeWithForName"
                components={{ strong: <strong /> }}
                values={{ name: slot.book.author.name }}
              />
            ) : (
              <Trans
                i18nKey="reader.madeWith"
                components={{ strong: <strong /> }}
              />
            )}
          </div>
        </div>
      );
  }
}
